import { NOYAU_CODES, type NoyauCode, type NoyauDraw, type NoyauScore } from '../schemas/noyau.js';

/**
 * The RANKING of a determination (noyau.md §4.2): the best note gives the
 * PRINCIPAL noyau, the next one the SECOND - so a second always exists.
 *
 * The code ranks, not the model: the analyst answers six notes and nothing else
 * (`noyauAnalystOutputSchema`), and this function is the one place they are
 * turned into two places. Pure, and in `shared` like every rule both a server
 * and a reader may have to replay.
 *
 * ## The draw is an ARGUMENT
 *
 * A tie that decides the principal or the second is settled BY CHANCE, and the
 * chance is recorded (N2): an order fixed in advance - the table's, for
 * instance - would always favour the same noyau, and the measure of §13 could
 * not tell a model's preference from a tie-break's. So the caller draws a
 * permutation of the six noyaux (the server, with a real source of randomness)
 * and hands it in; this function reads it only where scores are equal, and says
 * whether it did. Given the same notes and the same permutation it answers the
 * same thing, which is what lets the tests pin a tie without mocking a global.
 */

export type NoyauRanking = {
  readonly principal: NoyauCode;
  readonly second: NoyauCode;
  /** The tie the draw settled, or null when the notes alone decided both places. */
  readonly draw: NoyauDraw | null;
};

/** The index of each code in the table, for the `tied` list of a recorded draw. */
const TABLE_RANK: ReadonlyMap<NoyauCode, number> = new Map(
  NOYAU_CODES.map((code, index) => [code, index]),
);

function tableRank(code: NoyauCode): number {
  return TABLE_RANK.get(code) ?? NOYAU_CODES.length;
}

/**
 * The principal and the second of six notes, a tie broken by `drawOrder`.
 *
 * `drawOrder` is a PERMUTATION of the six codes: among equal notes, the one
 * drawn earlier ranks higher. Anything else is refused by a throw - a draw that
 * forgot a noyau would silently fall back on some other order, and the one
 * thing this function promises is that no fixed order ever decides.
 *
 * `scores` is the parsed answer of the analyst, six notes one per triplet
 * (`noyauScoresSchema`); fewer than two could name no second, and is refused the
 * same way.
 */
export function rankNoyaux(
  scores: readonly NoyauScore[],
  drawOrder: readonly NoyauCode[],
): NoyauRanking {
  const drawnAt = drawnAtOf(drawOrder);

  const ranked = [...scores].sort(
    (a, b) => b.score - a.score || drawnAt(a.noyau) - drawnAt(b.noyau),
  );
  const [principal, second] = ranked;
  if (principal === undefined || second === undefined) {
    throw new Error('A ranking needs at least two notes: a principal and a second.');
  }

  return {
    principal: principal.noyau,
    second: second.noyau,
    draw: drawOf(ranked, principal.score, second.score, drawnAt),
  };
}

/**
 * Where each noyau stands in a draw: the position it was drawn at, read by a
 * tie to decide it. Anything but a permutation of the six is refused by a throw -
 * a draw that forgot a noyau would silently fall back on some other order.
 */
function drawnAtOf(drawOrder: readonly NoyauCode[]): (code: NoyauCode) => number {
  const drawRank = new Map<NoyauCode, number>(drawOrder.map((code, index) => [code, index]));
  if (drawOrder.length !== NOYAU_CODES.length || drawRank.size !== NOYAU_CODES.length) {
    throw new Error('A draw is a permutation of the six noyaux.');
  }
  return (code) => drawRank.get(code) ?? NOYAU_CODES.length;
}

/**
 * The tie that decided a place, or null.
 *
 * Two or more at the best note: the draw gave BOTH places, and the tie is
 * recorded on `principal`. The best alone and two or more at the next note: the
 * draw gave the second place. A tie further down decides nothing a person will
 * see, and recording it would make the measure read a draw that played no part.
 */
function drawOf(
  ranked: readonly NoyauScore[],
  best: number,
  next: number,
  drawnAt: (code: NoyauCode) => number,
): NoyauDraw | null {
  const atBest = ranked.filter((entry) => entry.score === best);
  if (atBest.length >= 2) return recordedTie('principal', atBest, drawnAt);
  const atNext = ranked.filter((entry) => entry.score === next);
  if (atNext.length >= 2) return recordedTie('second', atNext, drawnAt);
  return null;
}

function recordedTie(
  place: NoyauDraw['place'],
  group: readonly { readonly noyau: NoyauCode }[],
  drawnAt: (code: NoyauCode) => number,
): NoyauDraw {
  const codes = group.map((entry) => entry.noyau);
  return {
    place,
    tied: [...codes].sort((a, b) => tableRank(a) - tableRank(b)),
    order: [...codes].sort((a, b) => drawnAt(a) - drawnAt(b)),
  };
}

/** One of the six noyaux as the ranking of the analyst in two passes reads it (analyste.md §8.3). */
export type RankableNoyau = {
  readonly noyau: NoyauCode;
  readonly score: number;
  /** Struck out by a level the code fixed (§6.4): ranked after every candidate, whatever its note. */
  readonly eliminated: boolean;
};

/** The ranking of the analyst in two passes: the places, the tie a draw settled, and the six in order. */
export type AnalystRanking = NoyauRanking & {
  /** The six noyaux, the best first: the candidates, then the eliminated. */
  readonly order: readonly NoyauCode[];
};

/**
 * The RANKING of the analyst in two passes (analyste.md §8.3, issue #196): the
 * CANDIDATES before the eliminated, whatever their notes - two notes at zero
 * cannot reverse the order -, the best note first inside each group; the first is
 * the principal and the next the second, eliminated or not, so a second still
 * always exists.
 *
 * A tie that decides the principal or the second is settled by the draw, the draw
 * is an ARGUMENT, and it is recorded (N2), exactly as `rankNoyaux` does it: two
 * noyaux tie only inside one group, an eliminated one never ties a candidate.
 * With a single candidate the second is the best eliminated, and a tie between
 * eliminated ones at the top is recorded on the `second` place.
 */
export function rankAnalyzedNoyaux(
  rows: readonly RankableNoyau[],
  drawOrder: readonly NoyauCode[],
): AnalystRanking {
  const drawnAt = drawnAtOf(drawOrder);
  const group = (row: RankableNoyau): number => (row.eliminated ? 1 : 0);
  const ranked = [...rows].sort(
    (a, b) => group(a) - group(b) || b.score - a.score || drawnAt(a.noyau) - drawnAt(b.noyau),
  );
  const [principal, second, third] = ranked;
  if (principal === undefined || second === undefined) {
    throw new Error('A ranking needs at least two notes: a principal and a second.');
  }
  const tied = (a: RankableNoyau, b: RankableNoyau): boolean =>
    group(a) === group(b) && a.score === b.score;

  let draw: NoyauDraw | null = null;
  if (tied(principal, second)) {
    draw = recordedTie(
      'principal',
      ranked.filter((row) => tied(row, principal)),
      drawnAt,
    );
  } else if (third !== undefined && tied(second, third)) {
    draw = recordedTie(
      'second',
      ranked.filter((row) => tied(row, second)),
      drawnAt,
    );
  }
  return {
    principal: principal.noyau,
    second: second.noyau,
    draw,
    order: ranked.map((row) => row.noyau),
  };
}

/** What a READING of a determination sorts its notes against: the notes, and the places they gave. */
export type RankedNoyauScores = {
  readonly scores: readonly NoyauScore[];
  readonly principal: NoyauCode | null;
  readonly second: NoyauCode | null;
  readonly draw: NoyauDraw | null;
  /**
   * The noyaux the levels eliminated, when the determination was made by the
   * analyst in two passes (`eliminatedNoyaux` of its analysis): they are listed
   * AFTER every candidate, whatever their notes - the order of
   * `rankAnalyzedNoyaux`. Absent, the notes alone decide, as before #196.
   */
  readonly eliminated?: readonly NoyauCode[] | undefined;
};

/**
 * The six notes as every reading LISTS them (noyau.md §9.1, §9.3, §10; issue
 * #152): the best first, and a tie in the order the RANKING settled it - the
 * principal, then the second, then the rest of a recorded draw in its drawn
 * order, then the table.
 *
 * One sort for the three surfaces that list the notes - the parcours, the
 * markdown export and `get_session` - because two of them sorted on the note
 * alone: on SGP 70 and GPS 70 with a draw that made GPS the principal, they
 * listed SGP first under a line announcing GPS. The ranking is `rankNoyaux`'s,
 * read off the determination; nothing here draws again.
 */
export function sortedNoyauScores(ranked: RankedNoyauScores): NoyauScore[] {
  const drawn = ranked.draw?.order ?? [];
  const struckOut = new Set(ranked.eliminated ?? []);
  const place = (code: NoyauCode): number => {
    if (code === ranked.principal) return 0;
    if (code === ranked.second) return 1;
    const inDraw = drawn.indexOf(code);
    return inDraw >= 0 ? 2 + inDraw : 2 + NOYAU_CODES.length + tableRank(code);
  };
  return [...ranked.scores].sort(
    (one, other) =>
      Number(struckOut.has(one.noyau)) - Number(struckOut.has(other.noyau)) ||
      other.score - one.score ||
      place(one.noyau) - place(other.noyau),
  );
}
